import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { aggridHeaderClass, SmallTitleSpan } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultSAL6030_R01DetailDto, SAL6030_R01DetailDto } from "./Dto_SAL6030_R01";
import { useSAL6030_R01Store } from "./Store_SAL6030_R01";

const DETAIL_COL_STATE_KEY = 'SAL6030_R01-detail-column-state';

interface Props {
	detailGridRef: RefObject<AgGridReact<SAL6030_R01DetailDto> | null>;
}

export const DetailTable_SAL6030_R01 = ({ detailGridRef }: Props) => {
	const {
		SAL6030_R01detailList,
		selectedSAL6030_R01Detail,
		setSelectedSAL6030_R01Detail,
		detailColumnState, setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = useSAL6030_R01Store();
	const [hoveredRow, setHoveredRow] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: "valueIndex",
		gridData: SAL6030_R01detailList,
		defaultGridData: defaultSAL6030_R01DetailDto,
		selectedRow: selectedSAL6030_R01Detail,
	});

	const headerNames: Record<string, string> = {};

	const baseColumnDefs: ColDef<SAL6030_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "자재코드", colId: "childItemcd", field: "childItemcd", width: 130,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "자재명", colId: "itemNm", field: "itemNm", width: 160,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 140,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "보관위치", colId: "locationNm", field: "locationNm", width: 120,
			headerClass: aggridHeaderClass,
		} as ColDef<SAL6030_R01DetailDto>,
		{
			headerName: "자재공급방법", colId: "materialIntypeNm", field: "materialIntypeNm", width: 110,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "자재단가", colId: "itemCost", field: "itemCost", width: 100,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "SMT비용(단가)", colId: "smtUnitCost", field: "smtUnitCost", width: 110,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "도급자재", colId: "contractQty", field: "contractQty", width: 90,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "사급자재", colId: "consignQty", field: "consignQty", width: 90,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "소요량", colId: "totalQty", field: "totalQty", width: 80,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "자품목단위", colId: "childItemUnit", field: "childItemUnit", width: 90,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "자재비", colId: "materialCost", field: "materialCost", width: 100,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "SMT비용(금액)", colId: "smtCost", field: "smtCost", width: 110,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "비용합계", colId: "totalCost", field: "totalCost", width: 100,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "고정손실량", colId: "fixedLoss", field: "fixedLoss", width: 100,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "손실률(%)", colId: "lossRate", field: "lossRate", width: 90,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) =>
				p.value != null ? `${p.value}%` : "0%",
		},
		{
			headerName: "비고", colId: "remark", field: "remark", width: 160,
			headerClass: aggridHeaderClass,
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const colDefs: ColDef<SAL6030_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<SAL6030_R01DetailDto>({
			baseColumnDefs: baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottom = useMemo(
		() => [
			{
				childItemcd: SAL6030_R01detailList.length.toLocaleString() + "건",
				materialCost: SAL6030_R01detailList.reduce((s, i) => s + (i.materialCost || 0), 0).toLocaleString(),
				smtCost: SAL6030_R01detailList.reduce((s, i) => s + (i.smtCost || 0), 0).toLocaleString(),
				totalCost: SAL6030_R01detailList.reduce((s, i) => s + (i.totalCost || 0), 0).toLocaleString(),
			},
		],
		[SAL6030_R01detailList]
	);

	const handleDetailClick = useCallback(
		(e: RowClickedEvent<SAL6030_R01DetailDto>) => {
			if (e.node.rowPinned === "bottom") return;
			const row = e.data;
			if (!row) return;
			setSelectedSAL6030_R01Detail(row);
		},
		[setSelectedSAL6030_R01Detail]
	);

	return (
		<div className="h-full flex flex-col">
			<SmallTitleSpan>소요자재 원가</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<SAL6030_R01DetailDto>
					enableCellCalculation
					gridRef={detailGridRef}
					paddedGridData={paddedGridData}
					initSelected={() => setSelectedSAL6030_R01Detail(undefined)}
					defaultRowData={defaultSAL6030_R01DetailDto}
					baseColumnDefs={detailColumnState ? colDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setDetailColumnState}
					localStorageKey={DETAIL_COL_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<SAL6030_R01DetailDto>
						ref={detailGridRef}
						rowData={paddedGridData}
						columnDefs={colDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<SAL6030_R01DetailDto>({
							idKey: "valueIndex",
							selectedList: selectedSAL6030_R01Detail?.valueIndex ? [selectedSAL6030_R01Detail.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleDetailClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottom}
						setHoveredRowIndex={setHoveredRow}
						columnStateKey={DETAIL_COL_STATE_KEY}
						setColumnState={setDetailColumnState}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
